/**
 * React component that is responsible for rendering an "information" card.
 *
 * Copyright (c) 2020 Coursera
 * Author: Kelvin Nguyen
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types'
import cx from 'classnames'
import Image from 'components/Image'
import Typography from 'components/theme/Typography'
import styles from './CTACard.module.scss'

export const CTACardPropsTypes = {
  className: PropTypes.string,
  backgroundColor: PropTypes.oneOf(['blue', 'white', 'lightBlue']),
  icon: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  linkText: PropTypes.string,
  href: PropTypes.string,
  flipped: PropTypes.bool,
  children: PropTypes.node,
}

const CTACard = ({
  className,
  backgroundColor,
  icon,
  title,
  subtitle,
  linkText,
  href,
  flipped,
  children,
}) => {
  const TitleIcon = useMemo(() => {
    const Title = (title) ? (
      <Typography variant="h1" align="center">
        {title}
      </Typography>
    ) : null

    const iconClass = cx({
      'mb-xl': !flipped,
    })

    const Icon = (icon?.src) ? (
      <Image
        src={icon.src}
        alt={icon?.alt || title}
        align="center"
        maxHeight="128px"
        className={iconClass} />
    ) : null

    return (flipped) ? (
      <>
        {Title}
        {Icon}
      </>
    ) : (
      <>
        {Icon}
        {Title}
      </>
    )
  }, [title, icon, flipped])

  const Subtitle = useMemo(() => (
    (subtitle) ? (
      <Typography variant="body1" align="center" className={cx('mt-sm mb-md mx-lg')}>
        {subtitle}
      </Typography>
    ) : null
  ), [subtitle])

  const Link = useMemo(() => (
    (href && linkText) ? (
      <a href={href} className={cx(styles.link)}>{linkText}</a>
    ) : null
  ), [href, linkText])

  const Body = useMemo(() => (
    (children) ? (
      <div className={cx(styles.ctaBody)}>
        {children}
      </div>
    ) : children
  ), [children])

  const ctaClass = cx('cta-card p-md', styles.ctaCard, className, {
    [`bg-${backgroundColor}`]: !!backgroundColor,
  })

  return (
    <div className={ctaClass}>
      <div className="cta-card__header">
        {TitleIcon}
        {Subtitle}
      </div>
      <div className="cta-card__content">
        {Body}
        {Link}
      </div>
    </div>
  )
}

CTACard.propTypes = CTACardPropsTypes
CTACard.defaultProps = {
  flipped: false,
}

export default CTACard
